import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { UsergroupProfile, UsergroupServiceQuality, UsergroupService, UsergroupQuality } from '../../models';

export interface SkillSelection {
  qualityId: number;
  name: string;
  serviceId: number;
  serviceNumber: string;
  serviceDescription: string;
  categoryNumber: number;
  categoryName?: string;
  userLevel: number;
  isSelected: boolean;
}

@Component({
  selector: 'app-add-skills',
  templateUrl: './add-skills.component.html',
  styleUrls: ['./add-skills.component.scss'],
  standalone: false
})
export class AddSkillsComponent implements OnInit {
  @Input() profile!: UsergroupProfile;
  @Input() groupId!: number;
  @Input() availableSkills!: UsergroupServiceQuality[];
  @Output() close = new EventEmitter<void>();
  @Output() skillsSaved = new EventEmitter<UsergroupProfile>();

  skillSelections: SkillSelection[] = [];
  filteredSkills: SkillSelection[] = [];
  searchText: string = '';
  categories: { number: number; name: string }[] = [];
  services: { number: string; name: string,categories: { number: number; name: string }[] }[] = [];

  constructor(private i18n: I18NextPipe) {}

  ngOnInit(): void {
    this.initializeSkills();
    this.filterSkills();
  }

  initializeSkills(): void {
    const existingSkillMap = new Map<number, number>();

    // Map existing skills and their levels
    if (this.profile.skills) {
      this.profile.skills.forEach(service => {
        service.qualities.forEach(quality => {
          existingSkillMap.set(quality.qualityId, quality.userLevel || 0);
        });
      });
    }

    // Build skill selections from available skills
    this.skillSelections = [];
    const categoryMap = new Map<number, string>();

    this.availableSkills.forEach(serviceQuality => {
      serviceQuality.qualities.forEach(quality => {
        const existingLevel = existingSkillMap.get(quality.qualityId) ?? null;
        const isSelected = existingLevel !== null;

        // Track categories
        if (quality.categoryNumber && !categoryMap.has(quality.categoryNumber)) {
          categoryMap.set(quality.categoryNumber, quality.categoryName || `Category ${quality.categoryNumber}`);
        }

        this.skillSelections.push({
          qualityId: quality.qualityId,
          name: quality.name,
          serviceId: serviceQuality.serviceId,
          serviceNumber: serviceQuality.serviceNumber,
          serviceDescription: serviceQuality.description,
          categoryNumber: quality.categoryNumber,
          categoryName: quality.categoryName,
          userLevel: existingLevel !== null ? existingLevel : 0,
          isSelected: isSelected
        });
      });
    });

    // Build categories list
    this.categories = Array.from(categoryMap.entries()).map(([number, name]) => ({ number,name })).sort((a, b) => a.number - b.number);

    // Build services list
    this.services = this.availableSkills.map(s => ({
      number: s.serviceNumber,
      name: s.description,
      categories: s.qualities.map(q => ({ number: q.categoryNumber, name: q.categoryName || `Category ${q.categoryNumber}` }))
        .filter((value, index, self) => index === self.findIndex((t) => t.number === value.number))
    })).sort((a, b) => a.number.localeCompare(b.number));

  }

  filterSkills(): void {
    let filtered = [...this.skillSelections];

    // Filter by search text
    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(skill =>
        skill.name?.toLowerCase().includes(search) ||
        skill.categoryName?.toLowerCase().includes(search)
      );
    }

    this.filteredSkills = filtered;
  }

  onSearchChange(): void {
    this.filterSkills();
  }

  getSkillsForCategory(serviceNumber: string, categoryNumber: number): SkillSelection[] {
    return this.filteredSkills.filter(s => s.serviceNumber === serviceNumber && s.categoryNumber === categoryNumber);
  }

  toggleSkillSelection(skill: SkillSelection): void {
    skill.isSelected = !skill.isSelected;
    if (!skill.isSelected) {
      skill.userLevel = 0;
    } else if (skill.userLevel === 0) {
      skill.userLevel = 1; // Default to level 1 when selecting
    }
  }

  onLevelChange(skill: SkillSelection, level: string): void {
    if(level === '') {
      skill.userLevel = 0;
      skill.isSelected = false;
      return;
    }
    skill.userLevel = parseInt(level, 10);
    if (skill.userLevel > 0 && !skill.isSelected) {
      skill.isSelected = true;
    }
  }

  closeDialog(): void {
    this.close.emit();
  }

  saveSkills(): void {
    // Group skills by service
    const serviceMap = new Map<number, UsergroupService>();

    this.skillSelections.forEach(skill => {
      if (skill.isSelected && skill.userLevel !== null) {
        if (!serviceMap.has(skill.serviceId)) {
          serviceMap.set(skill.serviceId, {
            serviceId: skill.serviceId,
            qualities: []
          });
        }

        const service = serviceMap.get(skill.serviceId)!;
        service.qualities.push({
          qualityId: skill.qualityId,
          categoryNumber: skill.categoryNumber,
          name: skill.name,
          userLevel: skill.userLevel,
          isFiltered: false,
          isCategoryFiltered: false,
          categoryName: skill.categoryName
        });
      }
    });

    // Update profile with new skills
    const updatedProfile: UsergroupProfile = {
      ...this.profile,
      skills: Array.from(serviceMap.values())
    };

    this.skillsSaved.emit(updatedProfile);
  }

  getSelectedSkillsCount(): number {
    return this.skillSelections.filter(s => s.isSelected).length;
  }

  hasChanges(): boolean {
    // Check if any skills have been added, removed, or levels changed
    const currentSkillMap = new Map<number, number>();

    if (this.profile.skills) {
      this.profile.skills.forEach(service => {
        service.qualities.forEach(quality => {
          currentSkillMap.set(quality.qualityId, quality.userLevel || 0);
        });
      });
    }

    // Check if selections match current profile
    for (const skill of this.skillSelections) {
      const currentLevel = currentSkillMap.get(skill.qualityId);
      if (skill.isSelected) {
        if (currentLevel === undefined || currentLevel !== skill.userLevel) {
          return true;
        }
      } else {
        if (currentLevel !== undefined) {
          return true;
        }
      }
    }

    return false;
  }



}

